/**
 * RegistrationForm.js
 * 
 * Simplified User Registration Form with Modern Dark Glassmorphism Theme
 * 
 * Updated By: Johnson Obhalloju
 * Date: December 5, 2024
 * 
 * MAJOR UPDATES:
 * ==============
 * 1. Simplified Form Structure:
 *    - Row 1: First Name | Last Name (2-column on desktop, 1-column on mobile)
 *    - Row 2: Email (full width)
 *    - Row 3: Password (full width)
 *    - Row 4: Mobile Number (full width)
 *    - Row 5: Captcha Section (responsive layout)
 *    - Row 6: Centered Register Button
 * 
 * 2. Removed Fields:
 *    - Country, Date of Birth, Marital Status
 *    - Gender, Baptized status
 *    - Profile Picture upload
 *    - Address
 * 
 * 3. Enhanced Responsiveness:
 *    - 2-column layout for First/Last Name on tablets and above
 *    - Single column layout on mobile devices
 *    - Responsive captcha section that adapts to screen size
 *    - Full-width register button on mobile
 * 
 * 4. Visual Enhancements:
 *    - Consistent dark glassmorphism theme
 *    - Gradient buttons with hover effects and shadows
 *    - Smooth transitions on all interactive elements
 *    - Consistent border colors using cyan accent (#4CA1AF)
 *    - Professional spacing and padding throughout
 * 
 * 5. Validation:
 *    - Required: First Name, Last Name, Email, Password, Mobile Number, Captcha
 *    - Mobile number must be exactly 10 digits
 *    - Password must meet complexity requirements
 *    - Email validation with duplicate checking
 * 
 * DESIGN SYSTEM:
 * ==============
 * - Primary Color: #4CA1AF (Cyan)
 * - Secondary Color: #2C3E50 (Dark Blue)
 * - Background: Semi-transparent white with blur
 * - Text: White with varying opacity for hierarchy
 * - Borders: Cyan with 0.2-0.5 opacity
 * - Shadows: Cyan glow effects on interactive elements
 */

"use client";
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  MenuItem,
  IconButton,
  InputAdornment,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import { Visibility, VisibilityOff, Refresh } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from "react-helmet";
import Toast from "../components/Toast";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import '../styles/phoneInput.css'; // Custom styles for dark theme

const RegistrationForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [orgName, SetOrgName] = useState("");
  const [orgPic, SetOrgPic] = useState("");
  const [orgId, SetOrgId] = useState("");
  const [userId, SetUserId] = useState("0");
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState(""); // Phone with country code

  const [existed, SetExisted] = useState('')
  const API_URL = process.env.REACT_APP_API_URL;

  const [captcha, setCaptcha] = useState("");
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const { id } = useParams();

  const location = useLocation();
  // console.log("Organization ID from URL:", id); 

  const queryParams = new URLSearchParams(location.search);

  const prayerId = queryParams.get("prayerId");
  const slotId = queryParams.get("slotId");
  const userId2 = queryParams.get("userId");

  // Captcha generator
  const handleRefreshCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // avoid confusing chars
    let result = "";
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(result);
  };

  useEffect(() => {
    handleRefreshCaptcha(); // generate captcha when component mounts
  }, []);

  useEffect(() => {

    const fetchOrgDetails = async () => {
      // Replace this with your actual API call
      const response = await fetch(`${API_URL}org-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: id
        }),


      });
      const data = await response.json();
      SetOrgName(data.data.org_name)
      SetOrgPic(data.data.logo)
      SetOrgId(data.data.id)

    }
    fetchOrgDetails(); // generate captcha when component mounts
  }, []);

  // ✅ Validation Schema - Simplified
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Must be at least 8 characters")
      .matches(/[A-Z]/, "Must contain an uppercase letter")
      .matches(/[a-z]/, "Must contain a lowercase letter")
      .matches(/[0-9]/, "Must contain a number")
      .matches(/[@$!%*?&]/, "Must contain a special character"),
    phone: Yup.string()
      .required("Mobile number is required")
      .min(7, "Mobile number is too short")
      .max(15, "Mobile number is too long"),
    captchaInput: Yup.string()
      .required("Captcha is required")
      .test("captcha-match", "Captcha does not match", function (value) {
        return value === captcha; // exact match, case-sensitive
      }),
  });

  // ✅ Formik
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
      captchaInput: "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    context: { captcha },
    enableReinitialize: true,
    onSubmit: async (values) => {


      setLoading(true);
      try {
        // ✅ Create FormData for data submission
        const formData = new FormData();

        // Append all fields
        Object.keys(values).forEach((key) => {
          if (key !== 'phone') { // Skip phone as we'll use the state value with country code
            formData.append(key, values[key]);
          }
        });

        // Add phone with country code from state
        formData.append("phone", phone);

        // Add extra fields
        formData.append("orgId", orgId);
        formData.append("existedUserId", userId);
        formData.append("existed", existed);

        formData.append("prayerId", prayerId || "");
        formData.append("slotId", slotId || "");
        formData.append("userId2", userId2 || "");

        const response = await fetch(`${API_URL}add-new-user`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          setToast({ open: true, message: data.message, severity: "success" });
          setTimeout(() => {
            window.location.reload();
          }, 4000);
        } else {
          setToast({ open: true, message: data.message, severity: "error" });
        }

      } catch (err) {
        console.error("Error submitting form:", err);
        setToast({ open: true, message: "Something went wrong!", severity: "error" });
      }
      setLoading(false);
    },
  });


  const checkEmailExists = async (email) => {
    if (!email) return false;
    try {
      SetExisted('')
      const res = await fetch(`${API_URL}check-user-exists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          org_id: orgId,
          email: email
        })
      });
      const data = await res.json();
      if (data.success && data.current_org) {

        SetExisted(true)

      }
      if (data.success && !data.current_org && data.data) {
        // auto-fill values from backend
        formik.setFieldValue("firstName", data.data.first_name || "");
        formik.setFieldValue("lastName", data.data.last_name || "");
        formik.setFieldValue("password", "AutoGenerated@123");
        SetUserId(data.data.id)


        SetExisted(false)
      }


    } catch (err) {
      console.error("Error checking email:", err);
      return false;
    }
  };

  const validateEmail = async (value) => {
    let error;
    if (!value) {
      error = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
      error = "Invalid email address";
    } else {
      const exists = await checkEmailExists(value);
      if (exists) {
        error = "Email already exists";
      }
    }
    return error;
  };


  return (
    <>
      <Helmet>
        <title>User Registration - Glocal Prayer Network</title>
      </Helmet>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: '800px',
            height: '800px',
            background: 'radial-gradient(circle, rgba(76, 161, 175, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'pulse 8s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-15%',
            left: '-10%',
            width: '700px',
            height: '700px',
            background: 'radial-gradient(circle, rgba(44, 62, 80, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'pulse 10s ease-in-out infinite reverse',
          },
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 0.8,
            },
            '50%': {
              transform: 'scale(1.1)',
              opacity: 0.5,
            },
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1600px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 1,
            p: { xs: 2, sm: 4, md: 5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 4, md: 10, lg: 12 },
              minHeight: { md: 'calc(100vh - 64px)' },
            }}
          >
            {/* Left Side - Logo and Branding */}
            <Box
              sx={{
                flex: { xs: 'none', md: 1 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                maxWidth: { xs: '100%', md: '500px' },
              }}
            >
              {/* Large Logo Display */}
              <Box
                sx={{
                  width: { xs: 200, sm: 280, md: 350 },
                  height: { xs: 200, sm: 280, md: 350 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.2) 0%, rgba(44, 62, 80, 0.2) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '3px solid rgba(76, 161, 175, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 4,
                  boxShadow: '0 0 60px rgba(76, 161, 175, 0.3), inset 0 0 40px rgba(76, 161, 175, 0.1)',
                  position: 'relative',
                  animation: 'glow 3s ease-in-out infinite',
                  '@keyframes glow': {
                    '0%, 100%': {
                      boxShadow: '0 0 60px rgba(76, 161, 175, 0.3), inset 0 0 40px rgba(76, 161, 175, 0.1)',
                    },
                    '50%': {
                      boxShadow: '0 0 80px rgba(76, 161, 175, 0.5), inset 0 0 60px rgba(76, 161, 175, 0.2)',
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    width: '85%',
                    height: '85%',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    padding: '20px',
                  }}
                >
                  <img
                    src={orgPic || "https://glocalprayer.net/prayer/public/assets/img/gp-logo.png"}
                    alt={orgName || "Glocal Prayer Network"}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Box>

              {/* Branding Text */}
              <Typography
                variant="h3"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 800,
                  letterSpacing: '-1px',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                {orgName || "Glocal Prayer Network"}
              </Typography>
            </Box>

            {/* Right Side - Registration Form */}
            {/* Main Form Container - Updated By Johnson Obhalloju */}
            {/* Increased maxWidth to 900px for better 2-column layout visibility */}
            <Box
              sx={{
                flex: { xs: 'none', md: 1 },
                width: '100%',
                maxWidth: '900px', // Increased from 650px to accommodate 2-column grid
              }}
            >
              {/* Glassmorphism Paper Component with Dark Theme */}
              <Paper
                elevation={0}
                className="gpn-fade-in"
                sx={{
                  padding: { xs: 3, sm: 4, md: 5 },
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.95) 0%, rgba(28, 40, 51, 0.95) 100%)',
                  backdropFilter: 'blur(30px)',
                  border: '1px solid rgba(76, 161, 175, 0.3)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  width: '100%',
                  maxHeight: { md: 'calc(100vh - 100px)' },
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(76, 161, 175, 0.5)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(76, 161, 175, 0.7)',
                    },
                  },
                }}
              >
                {/* Form Content Container */}
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    margin: '0 auto',
                  }}
                >
                  {/* Form Header */}
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: '#FFFFFF',
                        fontWeight: 700,
                        mb: 2,
                        letterSpacing: '-0.5px',
                        fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.25rem' },
                      }}
                    >
                      User Registration
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        fontWeight: 400,
                      }}
                    >
                      Join our prayer community
                    </Typography>
                  </Box>

                  <form onSubmit={formik.handleSubmit}>
                    {/* Perfect 2-Column Grid Layout */}
                    <Box sx={{
                      width: '100%',
                      '& input': {
                        outline: 'none !important',
                      },
                      '& textarea': {
                        outline: 'none !important',
                      },
                      '& .MuiOutlinedInput-root': {
                        outline: 'none !important',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        outline: 'none !important',
                      },
                    }}>
                      {/* Row 1: First Name | Last Name */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 3,
                          mb: 3,
                        }}
                      >
                        <TextField
                          label="First Name"
                          name="firstName"
                          fullWidth
                          autoComplete="off"
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                          helperText={formik.touched.firstName && formik.errors.firstName}
                          disabled={existed === false}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '56px',
                              color: '#FFFFFF',
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.3), inset -2px -2px 5px rgba(255, 255, 255, 0.05)',
                              '& fieldset': {
                                borderColor: 'rgba(76, 161, 175, 0.3)',
                                borderRadius: '12px',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(76, 161, 175, 0.5)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4CA1AF',
                                boxShadow: '0 0 10px rgba(76, 161, 175, 0.3)',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'rgba(255, 255, 255, 0.7)',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#FCA5A5',
                            },
                          }}
                        />
                        <TextField
                          label="Last Name"
                          name="lastName"
                          fullWidth
                          autoComplete="off"
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                          helperText={formik.touched.lastName && formik.errors.lastName}
                          disabled={existed === false}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '56px',
                              color: '#FFFFFF',
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.3), inset -2px -2px 5px rgba(255, 255, 255, 0.05)',
                              '& fieldset': {
                                borderColor: 'rgba(76, 161, 175, 0.3)',
                                borderRadius: '12px',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(76, 161, 175, 0.5)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4CA1AF',
                                boxShadow: '0 0 10px rgba(76, 161, 175, 0.3)',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'rgba(255, 255, 255, 0.7)',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#FCA5A5',
                            },
                          }}
                        />
                      </Box>

                      {/* Row 2: Email | Password */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 3,
                          mb: 3,
                        }}
                      >
                        <Box>
                          <TextField
                            label="Email"
                            name="email"
                            type="email"
                            fullWidth
                            autoComplete="off"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                            onInput={async (e) => {
                              const error = await validateEmail(e.target.value);
                              formik.setFieldError("email", error);
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '56px',
                                color: '#FFFFFF',
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '12px',
                                boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.3), inset -2px -2px 5px rgba(255, 255, 255, 0.05)',
                                '& fieldset': {
                                  borderColor: 'rgba(76, 161, 175, 0.3)',
                                  borderRadius: '12px',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(76, 161, 175, 0.5)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#4CA1AF',
                                  boxShadow: '0 0 10px rgba(76, 161, 175, 0.3)',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                              },
                              '& .MuiFormHelperText-root': {
                                color: '#FCA5A5',
                              },
                            }}
                          />
                          {existed === true && (
                            <Typography sx={{ color: '#FCA5A5', fontSize: '0.75rem', mt: 0.5, ml: 1 }}>
                              You already exist in this organization.
                            </Typography>
                          )}
                          {existed === false && (
                            <Typography sx={{ color: '#FCA5A5', fontSize: '0.75rem', mt: 0.5, ml: 1 }}>
                              You are already registered. You can proceed with the same details.
                            </Typography>
                          )}
                        </Box>
                        <TextField
                          label="Password"
                          name="password"
                          data-testid="password-field"
                          type={showPassword ? "text" : "password"}
                          fullWidth
                          autoComplete="new-password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.password && Boolean(formik.errors.password)}
                          helperText={formik.touched.password && formik.errors.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  aria-label="toggle password visibility"
                                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          disabled={existed === false}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              height: '56px',
                              color: '#FFFFFF',
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '12px',
                              boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.3), inset -2px -2px 5px rgba(255, 255, 255, 0.05)',
                              '& fieldset': {
                                borderColor: 'rgba(76, 161, 175, 0.3)',
                                borderRadius: '12px',
                              },
                              '&:hover fieldset': {
                                borderColor: 'rgba(76, 161, 175, 0.5)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#4CA1AF',
                                boxShadow: '0 0 10px rgba(76, 161, 175, 0.3)',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: 'rgba(255, 255, 255, 0.7)',
                            },
                            '& .MuiFormHelperText-root': {
                              color: '#FCA5A5',
                            },
                          }}
                        />
                      </Box>

                      {/* Row 3: Mobile Number | Captcha Section */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 3,
                          mb: 4,
                        }}
                      >
                        {/* Mobile Number with Country Code - Using react-phone-input-2 */}
                        <Box sx={{ width: '100%' }}>
                          <PhoneInput
                            country={'in'}  // Default to India
                            value={phone}
                            onChange={(value) => {
                              setPhone(value);
                              formik.setFieldValue('phone', value);
                            }}
                            onBlur={() => formik.setFieldTouched('phone', true)}
                            enableSearch={true}
                            searchPlaceholder="Search countries..."
                            inputProps={{
                              name: 'phone',
                              required: true,
                              autoComplete: 'tel',
                            }}
                          />
                          {formik.touched.phone && formik.errors.phone && (
                            <Typography
                              sx={{
                                color: '#FCA5A5',
                                fontSize: '0.75rem',
                                mt: 0.5,
                                ml: 1.75,
                              }}
                            >
                              {formik.errors.phone}
                            </Typography>
                          )}
                        </Box>

                        {/* Captcha Section - All elements in same row */}
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'flex-start',
                            width: '100%',
                          }}
                        >
                          {/* Captcha Input */}
                          <TextField
                            label="Enter Captcha"
                            name="captchaInput"
                            autoComplete="off"
                            value={formik.values.captchaInput}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.captchaInput && Boolean(formik.errors.captchaInput)}
                            helperText={formik.touched.captchaInput && formik.errors.captchaInput}
                            data-testid="captcha-input"
                            sx={{
                              flex: '1 1 auto',
                              minWidth: '100px',
                              maxWidth: '100%',
                              '& .MuiOutlinedInput-root': {
                                height: '56px',
                                color: '#FFFFFF',
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '12px',
                                boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.3), inset -2px -2px 5px rgba(255, 255, 255, 0.05)',
                                '& fieldset': {
                                  borderColor: 'rgba(76, 161, 175, 0.3)',
                                  borderRadius: '12px',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(76, 161, 175, 0.5)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#4CA1AF',
                                  boxShadow: '0 0 10px rgba(76, 161, 175, 0.3)',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                              },
                              '& .MuiFormHelperText-root': {
                                color: '#FCA5A5',
                              },
                            }}
                          />

                          {/* Captcha Display */}
                          <Box
                            data-testid="captcha-display"
                            sx={{
                              px: 2,
                              border: '2px solid rgba(76, 161, 175, 0.3)',
                              borderRadius: '12px',
                              fontWeight: 'bold',
                              letterSpacing: 4,
                              fontSize: '1.125rem',
                              color: '#4CA1AF',
                              background: 'rgba(255, 255, 255, 0.05)',
                              boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.3), inset -2px -2px 5px rgba(255, 255, 255, 0.05)',
                              textAlign: 'center',
                              minWidth: '100px',
                              height: '56px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              userSelect: 'none',
                              flexShrink: 0,
                            }}
                          >
                            {captcha}
                          </Box>

                          {/* Refresh Button */}
                          <IconButton
                            onClick={handleRefreshCaptcha}
                            sx={{
                              background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                              color: '#FFFFFF',
                              width: '56px',
                              height: '56px',
                              borderRadius: '12px',
                              boxShadow: '3px 3px 8px rgba(0, 0, 0, 0.4), -3px -3px 8px rgba(255, 255, 255, 0.05)',
                              flexShrink: 0,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                                transform: 'scale(1.05)',
                                boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.5), -4px -4px 12px rgba(255, 255, 255, 0.08)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                            aria-label="refresh captcha"
                          >
                            <Refresh />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Submit Button - Centered with generous spacing */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                        <Button
                          type="submit"
                          disabled={loading || existed}
                          variant="contained"
                          sx={{
                            py: 1.5,
                            px: 5,
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                            boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.4), -4px -4px 12px rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(76, 161, 175, 0.4)',
                            minWidth: { xs: '100%', sm: '320px' },
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                              boxShadow: '6px 6px 16px rgba(0, 0, 0, 0.5), -6px -6px 16px rgba(255, 255, 255, 0.08)',
                              transform: 'translateY(-3px)',
                            },
                            '&:disabled': {
                              background: 'rgba(255, 255, 255, 0.1)',
                              color: 'rgba(255, 255, 255, 0.3)',
                              boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.3)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {loading ? 'Submitting...' : 'Register'}
                        </Button>
                      </Box>
                    </Box>
                  </form>

                  {/* Footer */}
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '0.75rem',
                      }}
                    >
                      © 2024 Glocal Prayer Network. All rights reserved.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box >
      <Toast toast={toast} setToast={setToast} />
    </>
  );
};

export default RegistrationForm;
